<script lang="ts">
import type { CheckboxGroupItem, CheckboxGroupProps } from '@nuxt/ui';
import type { SelectFieldOptions } from '#layers/data-model';
import type { InputBlockProps } from '~/types/input-block';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';

interface InputCheckboxBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<CheckboxGroupProps, 'size' | 'variant' | 'color' | 'icon'> {
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_checkbox.name',
	description: '$t:blocks.input_checkbox.description',
	icon: 'lucide:square-check',
	category: 'input',
	fieldBinding: { supportedTypes: ({ typeOptions }) => typeOptions?.multiple ? ['select'] : [] },

	options: ({ form, values }) => form([
		{
			name: 'modelValue',
			model: true,
		},
		selectOptionsFormSchema({ options: values.options }),
		{
			name: 'multiple',
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'icon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'disabled',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'readonly',
		},
	]),
}</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { defineBlock, useBlockInEditorContext } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { selectOptionsFormSchema } from '#layers/data-model';
import { useDisabledNotice, useReadonly } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';
import { useSelectOptions } from '~/composables/use-select-options';

const props = defineProps<InputCheckboxBlockProps>();
const model = defineModel<any>();

;

const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const forwarded = reactivePick(props, 'size', 'variant', 'color');

const { normalizedOptions: items } = useSelectOptions<CheckboxGroupItem>(() => props.options ?? []);

const { isPreview } = useBlockInEditorContext();
const previewItems = computed<CheckboxGroupItem[]>(() => Array.from({ length: 3 }, (_, i) => ({
	value: `option-${i + 1}`,
	label: `Option ${i + 1}`,
}) satisfies CheckboxGroupItem));
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DCheckboxGroup
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:aria-readonly="readonly"
			:items="isPreview ? previewItems : items"
			:ui="{
				item: props.readonly ? 'pointer-events-none' : '',
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
